import type React from "react"

import { useState, useRef, useEffect } from "react"
import "./time-range-dropdown.css"

interface TimeRangeDropdownProps {
  timeRanges: string[]
  defaultTimeRange: string
  onTimeRangeChange: (timeRange: string) => void
  className?: string
}

const TimeRangeDropdown: React.FC<TimeRangeDropdownProps> = ({
  timeRanges,
  defaultTimeRange,
  onTimeRangeChange,
  className = "",
}) => {
  const [selectedTimeRange, setSelectedTimeRange] = useState(defaultTimeRange)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const handleTimeRangeChange = (timeRange: string) => {
    setSelectedTimeRange(timeRange)
    setIsDropdownOpen(false)
    onTimeRangeChange(timeRange)
  }

  return (
    <div className={`time-range-dropdown ${className}`} ref={dropdownRef}>
      <button
        className="time-range-button"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        aria-haspopup="listbox"
        aria-expanded={isDropdownOpen}
      >
        {selectedTimeRange}
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={`dropdown-arrow ${isDropdownOpen ? "dropdown-arrow-open" : ""}`}
        >
          <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {isDropdownOpen && (
        <ul className="time-range-options" role="listbox">
          {timeRanges.map((range) => (
            <li
              key={range}
              className={`time-range-option ${range === selectedTimeRange ? "time-range-option-selected" : ""}`}
              onClick={() => handleTimeRangeChange(range)}
              role="option"
              aria-selected={range === selectedTimeRange}
            >
              {range}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default TimeRangeDropdown
